import type { ReactNode } from 'react';
import { Alert, Typography } from '@arco-design/web-react';
import {
  IconClockCircle,
  IconExclamationCircle,
  IconMessage,
  IconNotification,
  IconUser,
} from '@arco-design/web-react/icon';
import WorkbenchOperations from '../../operations/components/WorkbenchOperations';
import type { PortalApplication, PortalIdentity } from '../../../model/portalModels';
import type { PortalQuickTask } from '../../operations/types/portal.types';
import type { PortalPageIdentity } from '../../pages/api/pagesApi';
import type {
  PortalWorkbenchData,
  PortalWorkbenchMetric,
  WorkbenchMetricKey,
} from '../model/workbenchModels';
import { shanghaiWorkbenchTime } from '../model/workbenchModels';

const { Text } = Typography;

type PortalWorkbenchViewProps = {
  applications: PortalApplication[];
  identity: PortalIdentity;
  onSessionExpired: () => void;
  data: PortalWorkbenchData;
  error?: string;
  onNavigate: (path: string) => void;
  onOpenOperationList: () => void;
  onSaveFavorites: (operationIds: string[]) => Promise<void>;
  onSavePageFavorites: (pages: PortalPageIdentity[]) => Promise<void>;
  onReloadPageFavorites: () => Promise<void>;
  onApproveQuickTask: (task: PortalQuickTask) => Promise<void>;
};

function metricIcon(key: WorkbenchMetricKey): ReactNode {
  if (key === 'urge') return <IconExclamationCircle />;
  if (key === 'cc-unread') return <IconMessage />;
  if (key === 'notification') return <IconNotification />;
  return <IconClockCircle />;
}

function WorkbenchMetricCard(props: {
  metric: PortalWorkbenchMetric;
  onSelect: () => void;
}) {
  return (
    <button
      className={[
        'portal-summary-metric',
        `portal-summary-metric--${props.metric.key}`,
      ].filter(Boolean).join(' ')}
      type="button"
      onClick={props.onSelect}
    >
      <span className="portal-summary-metric__icon">{metricIcon(props.metric.key)}</span>
      <span className="portal-summary-metric__main">
        <span>{props.metric.label}</span>
        <strong>{props.metric.value}<small>{props.metric.unit}</small></strong>
      </span>
      <span className="portal-summary-metric__note">{props.metric.note}</span>
    </button>
  );
}

function PortalWorkbenchView(props: PortalWorkbenchViewProps) {
  const { data, onNavigate } = props;
  const time = shanghaiWorkbenchTime();
  return (
    <div className="reference-workspace-page portal-workbench">
      {props.error ? <Alert type="error" content={props.error} /> : null}
      <section className="reference-workspace-hero portal-workbench__greeting">
        <button className="portal-workbench__profile" type="button" onClick={() => onNavigate('/portal/personal')}>
          <span className="portal-user-avatar portal-user-avatar--workbench">
            <IconUser className="portal-user-avatar__fallback" aria-hidden="true" />
            {data.profile.avatarUrl
              ? <img className="portal-user-avatar__image"
                  src={data.profile.avatarUrl} alt=""
                  referrerPolicy="no-referrer"
                  onError={event => { event.currentTarget.hidden = true; }} /> : null}
          </span>
          <span>
            <strong>{data.profile.displayName}，{time.greeting}</strong>
            <small>{data.profile.organizationName} · {data.profile.roleName}</small>
          </span>
        </button>
        <div className="portal-workbench__greeting-aside">
          <span className="portal-workbench__date">{time.dateText}</span>
          <Text type="secondary">优先处理催办事项，及时查看抄送和通知。</Text>
        </div>
      </section>

      <section className="portal-workbench__metrics" aria-label="任务指标">
        {data.metrics.map(metric => (
          <WorkbenchMetricCard
            key={metric.key}
            metric={metric}
            onSelect={() => onNavigate(
              metric.key === 'notification'
                ? '/portal/notifications' : '/portal/todos',
            )}
          />
        ))}
      </section>

      <WorkbenchOperations
        applications={props.applications}
        identity={props.identity}
        onSessionExpired={props.onSessionExpired}
        operationLists={data.operationLists}
        initialFavoriteIds={data.favoriteOperationIds}
        quickTasks={data.quickTasks}
        notificationPreviews={data.notificationPreviews}
        onNavigate={onNavigate}
        onOpenOperationList={props.onOpenOperationList}
        onSaveFavorites={props.onSaveFavorites}
        onSavePageFavorites={props.onSavePageFavorites}
        favoritePages={data.favoritePages}
        pageFavoriteError={data.pageFavoriteError}
        onReloadPageFavorites={props.onReloadPageFavorites}
        onApproveQuickTask={props.onApproveQuickTask}
      />
    </div>
  );
}

export default PortalWorkbenchView;
